import { memo, useCallback, useRef } from 'react';
import classNames from 'classnames';
import Tooltip, { placementsList } from '@picsart/rc/old/Tooltip';
import SvgIcon from '@picsart/rc/old/SvgIcon';
import { useTranslator } from '@picsart/rc/services/localization';

import useMount from 'hooks/common/useMount';
import { UndoRedoTypes } from 'components/common/UndoRedo';

import useStyles from './styles';
import testIds from './testIds';

interface IProps {
  step: number;
  historyLength: number;
  disabled: boolean;
  onClickHandler: (value: 1 | -1, type: UndoRedoTypes) => void;
  getHeight?: (height: number) => void;
  minStep?: number;
}

const UndoRedo = ({
  step, historyLength, onClickHandler, getHeight, disabled, minStep = -1,
}: IProps) => {
  const classes = useStyles();
  const translate = useTranslator();

  const undoRedoContainerRef = useRef<HTMLDivElement>(null);

  useMount(() => {
    if (!undoRedoContainerRef.current) {
      return;
    }

    getHeight && getHeight(undoRedoContainerRef.current.clientHeight);
  });

  const undo = useCallback(() => {
    if (step <= -1 || disabled) {
      return;
    }

    onClickHandler(-1, UndoRedoTypes.UNDO);
  }, [step, onClickHandler, disabled]);

  const redo = useCallback(() => {
    if (step >= historyLength || disabled) {
      return;
    }

    onClickHandler(1, UndoRedoTypes.REDO);
  }, [step, historyLength, onClickHandler, disabled]);

  return (
    <div
      className={`${classes.undoRedoContainer} we-undoRedo`}
      ref={undoRedoContainerRef}
    >
      <Tooltip content={translate('Undo')} placement={placementsList.top}>
        <button
          className={classNames({ [classes.active]: (step > minStep && !disabled) }, classes.undoRedoIcon, 'we-undoRedo')}
          onClick={undo}
          data-testid={testIds.undoBtn}
        >
          <SvgIcon name="undo-editor" className={classes.undoRedo} />
        </button>
      </Tooltip>
      <Tooltip content={translate('Redo')} placement={placementsList.top}>
        <button
          className={classNames({ [classes.active]: (historyLength - 1 > step && !disabled) }, classes.undoRedoIcon, 'we-undoRedo')}
          onClick={redo}
          data-testid={testIds.redoBtn}
        >
          <SvgIcon name="redo-editor" className={classes.undoRedo} />
        </button>
      </Tooltip>
    </div>
  );
};

export default memo(UndoRedo);
